/** @jsxImportSource theme-ui @jsxRuntime classic */
import { H } from '@hzdg/sectioning';
import { Arrow } from '@tishman/icons';
import React from 'react';

import { Flex, Container, Link, Button, Box } from '..';

import { CarouselContextProvider } from './CarouselContext';
import { CarouselList } from './CarouselList';
import { CarouselNavigation } from './CarouselNavigation';
import { TicketCarouselCard } from './TicketCarouselCard';

import type { TicketCarouselCardProps } from './TicketCarouselCard';

export interface TicketCarouselProps {
  tickets?: ReadonlyArray<Readonly<TicketCarouselCardProps['ticket']>>;
  title?: string;
  isComparisonModalOpen?: boolean;
  localizedModalCTA?: { label: string; slug: string };
  setIsComparisonModalOpen?: (isComparisonModalOpen: boolean) => void;
}

interface ExtendedTicketCarouselProps extends TicketCarouselProps {
  cta?: {
    label?: string;
    href?: string;
  };
}

/**
 * `TicketCarousel` will render a list of `data` in a `CarouselList`
 * with a `title` and a `CarouselNavigation`.
 *
 * @see https://tishman.netlify.app/components/carousel
 */
export function TicketCarousel({
  title = 'Ticket Offerings',
  tickets = [],
  cta,
  localizedModalCTA,
  isComparisonModalOpen,
  setIsComparisonModalOpen,
}: ExtendedTicketCarouselProps): React.JSX.Element {
  const showCTA = cta?.label && cta?.href;

  return (
    <Flex
      data-module="TicketCarousel"
      sx={{
        flexDirection: 'column',
        position: 'relative',
        pt: [4, 7],
        pb: [4, 5],
      }}
    >
      <Container mb={[3, null, 5]} px={[3, 5]}>
        <Flex
          sx={{
            flexDirection: ['column', null, 'row'],
          }}
        >
          <H
            sx={{
              variant: 'styles.h1',
              fontSize: [6, 7], // FIXME: mirrors styles.h1, but using the variant won't get the 7 value to appear
              fontFamily: 'headingSecondary',
            }}
          >
            {title}
          </H>
          {localizedModalCTA && setIsComparisonModalOpen && (
            <Box
              sx={{
                alignSelf: ['flex-start', null, 'center'],
                display: 'flex',
                justifyContent: 'flex-end',
                mx: ['0', null, '50px'],
              }}
            >
              <Button
                id={`tor-compare-tickets-button`}
                onClick={() => {
                  setIsComparisonModalOpen(!isComparisonModalOpen);
                }}
                variant="underline"
              >
                {localizedModalCTA.label}
              </Button>
            </Box>
          )}
          {showCTA && (
            <Flex
              sx={{
                alignItems: 'center',
                zIndex: '2',
                width: 'fit-content',
              }}
            >
              <Link
                href={cta?.href || ''}
                sx={{ display: 'flex', columnGap: 2 }}
                variant="underline"
              >
                {cta.label}
                <Arrow aria-hidden style={{ marginTop: '2px' }} />
              </Link>
            </Flex>
          )}
        </Flex>
      </Container>
      <CarouselContextProvider initialValue={{ pages: tickets.length }}>
        <CarouselList
          gap={[3, 4]}
          gapEnd={[3, 5]}
          gapStart={[3, 5]}
          maxWidth="container"
          name={title}
        >
          {tickets.map((ticket) => (
            <TicketCarouselCard key={ticket.id} ticket={ticket} />
          ))}
        </CarouselList>
        <Container
          sx={{
            alignSelf: 'center',
            display: 'flex',
            justifyContent: 'flex-end',
            mt: [4, 0],
            mb: [3, 0],
            position: ['static', 'absolute'],
          }}
        >
          <CarouselNavigation />
        </Container>
      </CarouselContextProvider>
    </Flex>
  );
}
